import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';

interface TaxonomyNodeData {
  label: string;
  type: 'root' | 'dimension' | 'category' | 'label';
  detected?: boolean;
  confidence?: number;
  signalCount?: number;
  color?: string;
  description?: string;
}

const TaxonomyNode: React.FC<NodeProps<TaxonomyNodeData>> = ({ data, selected }) => {
  const { label, type, detected, confidence, signalCount, description } = data;

  const getNodeStyles = () => {
    const baseStyles =
      'min-w-[160px] rounded-2xl border px-4 py-3 text-left shadow-[var(--shadow-card)] transition-[transform,border-color,box-shadow,background-color] duration-200';
    
    switch (type) {
      case 'root':
        return `${baseStyles} border-foreground/10 bg-foreground text-background min-w-[200px] text-center`;
      case 'dimension':
        return `${baseStyles} border-primary/20 bg-primary text-primary-foreground min-w-[180px] font-semibold`;
      case 'category':
        return `${baseStyles} border-[color:rgba(146,78,255,0.25)] bg-[linear-gradient(135deg,rgba(146,78,255,0.96),rgba(67,66,245,0.96))] text-white min-w-[180px]`;
      case 'label':
        return `${baseStyles} ${
          detected
            ? 'border-[var(--go-berry)]/20 bg-[color:rgba(157,11,77,0.06)] text-foreground'
            : 'border-border bg-background text-foreground'
        } min-w-[160px]`;
      default:
        return baseStyles;
    }
  };

  const getHandleStyle = () => {
    switch (type) {
      case 'root':
        return { background: '#2b2b3a' };
      case 'dimension':
        return { background: '#4342F5' };
      case 'category':
        return { background: '#924EFF' }; // purple
      case 'label':
        return { background: detected ? '#92204D' : '#07A0C3' }; // berry : teal
      default:
        return {};
    }
  };

  return (
    <div 
      className={`${getNodeStyles()} ${selected ? 'ring-2 ring-primary/40' : ''} ${type !== 'root' ? 'hover:-translate-y-0.5' : ''}`}
      title={description}
    >
      {type !== 'root' && (
        <Handle
          type="target"
          position={Position.Left}
          style={getHandleStyle()}
          className="w-2 h-2"
        />
      )}
      
      <div className="font-medium text-sm">
        {label}
      </div>
      
      {type === 'label' && detected && confidence !== undefined && (
        <div className="mt-1 text-[11px] font-medium text-muted-foreground">
          {(confidence * 100).toFixed(0)}% confidence
        </div>
      )}
      
      {signalCount !== undefined && signalCount > 0 && (
        <div className="mt-1 text-[11px] text-muted-foreground">
          {signalCount} signals
        </div>
      )}
      
      {type !== 'label' && (
        <Handle
          type="source"
          position={Position.Right}
          style={getHandleStyle()}
          className="w-2 h-2"
        />
      )}
    </div>
  );
};

export default memo(TaxonomyNode);
